import { useCallback } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import InfiniteLoader from "react-window-infinite-loader";

import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import ListEntitiesNew from "components/ListEntitiesNew";
import MetaInfoListItem from "components/MetaInfoList/Item";
import MetaInfoProvider from "components/MetaInfoList/Provider";
import PaginationIndicator from "components/PaginationIndicator";
import SearchInput from "components/SearchInput";
import TimestampMetaInfo from "components/Timestamp/MetaInfo";
import ViewHeaderTitle from "components/ViewHeader/Title";
import NotFoundPage from "components/error/NotFoundPage";
import { Space } from "components/icons/generated";
import PageLoading from "components/loading/PageLoading";
import BadgeNext from "ds/components/BadgeNext";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import PageInfoHeader from "ds/components/PageInfoHeader";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useCanManageVCSIntegration from "shared/VCS/access/useCanManageVCSIntegration";
import { VcsProvider } from "types/generated";
import { useLayoutActions } from "views/Account/Integrations/hooks/useLayoutActions";
import { useLayoutHeading } from "views/Account/Integrations/hooks/useLayoutHeading";
import { useLayoutMeta } from "views/Account/Integrations/hooks/useLayoutMeta";
import { IntegrationsOutletContext } from "views/Account/Integrations/types";

import { showVCSIntegrationDetailsDrawer } from "../VcsShared/components/Details";
import VCSIntegrationFeedback from "../VcsShared/components/IntegrationFeedback";
import { getEditIntegrationURL } from "../VcsShared/helpers";
import VCSIntegrationDropdownActions from "./DropdownActions";
import VCSIntegrationEmpty from "./Empty";
import VCSIntegrationFiltersLayout from "./FiltersLayout";
import VCSIntegrationUsedByVirtualizedListItem from "./ListItem/Virtualized";
import { ITEMS_LIMIT, VCS_INTEGRATION_VIEW_FILTERS_KEY } from "./constants";
import { useSearchVCSIntegrationUsedBy } from "./useSearchVCSIntegrationUsedBy";

const VCSIntegration = () => {
  const navigate = useNavigate();
  const { integration: integrationDetails } = useOutletContext<IntegrationsOutletContext>();

  const {
    integration,
    usedByItems,
    isPageLoading,
    isPageEmpty,
    isPageRefetching,
    totalEntitiesCount,
    error,
    hasNextPage,
    loadMoreItems,
  } = useSearchVCSIntegrationUsedBy();
  useTitle(integration?.name ? `Integrations · ${integration.name}` : "Integrations");

  const handleOpenDetailsDrawer = () => {
    showVCSIntegrationDetailsDrawer({
      item: integration,
      canEdit: canManageVCSIntegrations,
    });
  };

  const handleIntegrationEdit = useCallback(() => {
    if (integration?.id) {
      navigate(getEditIntegrationURL(integration.provider, integration.id));
    }
  }, [navigate, integration]);

  const isItemLoaded = (value: number) => !hasNextPage || value < usedByItems.length;

  // FYI: incorrect type as integration.space can be undefined in certain circumstances
  const canManageVCSIntegrations = useCanManageVCSIntegration()(integration);

  const handleIntegrationDelete = useCallback(() => {
    navigate(`/integrations/${integrationDetails?.id}`);
  }, [navigate, integrationDetails]);

  useLayoutHeading(
    <>
      <ViewHeaderTitle>{integration?.name}</ViewHeaderTitle>
      {integration?.isDefault && <BadgeNext variant="gray" type="strong" text="DEFAULT" />}
    </>,
    integration?.name ?? ""
  );
  useLayoutMeta(
    <>
      <MetaInfoListItem
        icon={Space}
        linkText={integration?.space?.name}
        href={`/spaces/${integration?.space?.id}`}
      />
      <MetaInfoProvider provider={integration?.provider as VcsProvider} />
      <TimestampMetaInfo timestamp={integration?.updatedAt ?? 0} />
    </>
  );
  useLayoutActions(
    <>
      <Button variant="secondary" onPress={handleOpenDetailsDrawer}>
        Details
      </Button>

      {canManageVCSIntegrations && integration && (
        <VCSIntegrationDropdownActions
          integration={integration}
          onEdit={handleIntegrationEdit}
          totalEntitiesCount={totalEntitiesCount}
          onDelete={handleIntegrationDelete}
        />
      )}
    </>
  );

  useBreadcrumbs(
    [
      {
        title: "Integrations",
        link: "/integrations",
      },
      {
        title: integrationDetails?.name || "",
        link: `/integrations/${integrationDetails?.id}`,
      },
      {
        title: integration?.name || "",
      },
    ],
    [integration?.name]
  );

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (isPageLoading) {
    return <PageLoading />;
  }

  if (!integration) {
    return <NotFoundPage />;
  }

  return (
    <>
      <VCSIntegrationFeedback integration={integration} viewPageMode />

      <PageInfoHeader title="Used by">
        {!isPageEmpty && (
          <Box direction="row" align="center" gap="0 large">
            <PaginationIndicator
              currentCount={usedByItems.length}
              totalCount={totalEntitiesCount}
              loading={isPageRefetching}
              minimumLoadingDuration={200}
            />

            <SearchInput
              placeholder="Search by name"
              filtersOrderSettingsKey={VCS_INTEGRATION_VIEW_FILTERS_KEY}
            />
          </Box>
        )}
      </PageInfoHeader>

      {!isPageEmpty && <VCSIntegrationFiltersLayout />}
      {isPageEmpty && <VCSIntegrationEmpty />}

      {!isPageEmpty && (
        <InfiniteLoader
          isItemLoaded={isItemLoaded}
          itemCount={hasNextPage ? usedByItems.length + ITEMS_LIMIT : usedByItems.length}
          loadMoreItems={loadMoreItems}
        >
          {({ onItemsRendered }) => (
            <ListEntitiesNew
              itemCount={usedByItems.length}
              itemProps={{
                items: usedByItems,
              }}
              virtualizedItem={VCSIntegrationUsedByVirtualizedListItem}
              itemKey={(index) => usedByItems[index].stackId}
              onItemsRendered={onItemsRendered}
            />
          )}
        </InfiniteLoader>
      )}
    </>
  );
};

export default VCSIntegration;
